/* ShowApp — galería visual de la app.
   Mobile: carrusel horizontal con snap.
   Desktop: grilla 3 columnas con tilt on hover. */

.section {
  position: relative;
  isolation: isolate;
  background: var(--blue-50);
  padding-block: var(--space-20);
  overflow: hidden;
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-24);
  }
}

@media (min-width: 1024px) {
  .section {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

/* ─── Grilla ─── */

.gallery {
  list-style: none;
  padding: 0;
  margin: var(--space-12) 0 0;

  /* Mobile: carrusel horizontal con snap */
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 78%;
  gap: var(--space-5);
  overflow-x: auto;
  overflow-y: visible;
  scroll-snap-type: x mandatory;
  scroll-padding: var(--space-4);
  padding-block: var(--space-2);
  padding-inline: var(--space-4);
  margin-inline: calc(var(--space-4) * -1);
  scrollbar-width: none;
  perspective: 1400px;
}

.gallery::-webkit-scrollbar {
  display: none;
}

@media (min-width: 768px) {
  .gallery {
    grid-auto-flow: initial;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-columns: initial;
    gap: var(--space-10);
    overflow: visible;
    scroll-snap-type: none;
    padding-inline: 0;
    margin-inline: 0;
    margin-top: var(--space-16);
  }
}

@media (min-width: 1024px) {
  .gallery {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-8);
  }
}

/* ─── Item ─── */

.item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  scroll-snap-align: center;

  /* Reveal — fade + slide-up, stagger por --i */
  opacity: 0;
  transform: translateY(24px);
  transition:
    opacity 700ms var(--ease-decelerate),
    transform 700ms var(--ease-decelerate);
  transition-delay: calc(var(--i, 0) * 120ms);
  will-change: opacity, transform;
}

.revealed .item {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .item {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ─── Phone frame ─── */

.phone {
  --rx: 0deg;
  --ry: 0deg;

  position: relative;
  width: 100%;
  max-width: 240px;
  aspect-ratio: 9 / 19.5;
  background: linear-gradient(160deg, #2a2620 0%, #0f0d0a 100%);
  border-radius: 34px;
  padding: 7px;
  box-shadow:
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.06),
    inset 0 0 0 3px rgba(0, 0, 0, 0.4),
    var(--shadow-xl);

  transform-style: preserve-3d;
  transform: perspective(1400px) rotateX(var(--rx)) rotateY(var(--ry)) translateZ(0);
  transition:
    transform var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

.phone:hover {
  box-shadow:
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.08),
    inset 0 0 0 3px rgba(0, 0, 0, 0.4),
    var(--shadow-2xl);
}

@media (prefers-reduced-motion: reduce) {
  .phone {
    transform: none;
    transition: none;
  }
}

.screen {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 27px;
  background: var(--color-surface);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ─── Caption ─── */

.itemTitle {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-base);
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
  text-align: center;
}

.itemCaption {
  font-family: var(--font-body);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-muted);
  margin: calc(var(--space-1) * -1) 0 0;
  text-align: center;
  max-width: 26ch;
}

/* ─── Swipe hint (solo mobile) ─── */

.swipeHint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin: var(--space-6) 0 0;
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-text-subtle);
}

.swipeHint::before,
.swipeHint::after {
  content: "";
  width: 24px;
  height: 1px;
  background: var(--color-border);
}

@media (min-width: 768px) {
  .swipeHint {
    display: none;
  }
}

/* ════════════════════════════════════════════════════════════════════════════
   Mockup: Perfil profesional
   ════════════════════════════════════════════════════════════════════════════ */

.profileSurface {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--sand-50);
  overflow: hidden;
}

.profileHeader {
  position: relative;
  background: var(--blue-700);
  color: var(--color-text-inverse);
  padding: 26px 14px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.profileBack {
  position: absolute;
  top: 14px;
  left: 14px;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--color-text-inverse);
  border: none;
  padding: 0;
}

.profileAvatarWrap {
  position: relative;
  margin-bottom: 8px;
}

.profileAvatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 18px;
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-md);
}

/* Color del avatar del header (jade) — se combina con .profileAvatar */
.avatar1 {
  background: var(--jade-500);
}

.profileCheck {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: var(--radius-full);
  background: var(--jade-500);
  color: var(--color-text-inverse);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px var(--blue-700);
}

.profileCheck svg {
  width: 8px;
  height: 8px;
}

.profileNameLg {
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 13px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--color-text-inverse);
  text-align: center;
  margin: 0;
}

.profileCategory {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.86);
  margin-top: 2px;
}

.profileSpecUpper {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 6px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
  margin-top: 2px;
}

/* Card "NUEVO ★" — colgada del borde header/body */
.profileNuevoWrap {
  display: flex;
  justify-content: center;
  margin: -22px 0 0;
  position: relative;
  z-index: 2;
}

.profileNuevoCard {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  padding: 8px 18px;
  box-shadow: var(--shadow-md);
  color: var(--color-text);
}

.profileNuevoLabel {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 7px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text);
}

.profileNuevoCard svg {
  width: 12px;
  height: 12px;
  color: var(--color-text);
}

.profileBody {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 10px 6px;
  overflow: hidden;
}

.profileCard {
  background: var(--color-surface);
  border-radius: var(--radius-md);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: var(--shadow-sm);
}

.profileSectionTitle {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
}

.profileMapCard {
  position: relative;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: #EFEAD7;
  aspect-ratio: 16 / 9;
}

.profileMapSvg {
  display: block;
  width: 100%;
  height: 100%;
}

.profileMapBtn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface);
  border-radius: var(--radius-full);
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
}

/* ─── Modalidad de trabajo (switches) ─── */

.modalityList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.modalityRow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 2px;
}

.modalityRow + .modalityRow {
  border-top: 1px solid var(--color-border-subtle);
}

.modalityIcon {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-full);
  background: var(--blue-50);
  color: var(--blue-500);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modalityLabel {
  flex: 1;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 9px;
  color: var(--color-text);
  letter-spacing: -0.005em;
}

.modalitySwitch {
  flex: none;
  position: relative;
  width: 24px;
  height: 14px;
  border-radius: var(--radius-full);
  background: var(--sand-300);
  transition: background var(--duration-base) var(--ease-standard);
}

.modalitySwitchOn {
  background: var(--blue-500);
}

.modalityKnob {
  position: absolute;
  top: 1.5px;
  left: 1.5px;
  width: 11px;
  height: 11px;
  border-radius: var(--radius-full);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  transition: transform var(--duration-base) var(--ease-standard);
}

.modalitySwitchOn .modalityKnob {
  transform: translateX(10px);
}

/* ════════════════════════════════════════════════════════════════════════════
   Mockup: Home — header de ubicación + selector de categorías
   ════════════════════════════════════════════════════════════════════════════ */

.homeSurface {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--sand-50);
  overflow: hidden;
}

.homeHeader {
  background: var(--blue-700);
  color: var(--color-text-inverse);
  padding: 30px 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.homeHeaderLoc {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.homeLocLabel {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 7px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.homeLocValue {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: -0.01em;
  color: var(--color-text-inverse);
}

.homeLocValue svg {
  flex: none;
  color: var(--color-text-inverse);
}

.homeAvatar {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-full);
  background: var(--jade-500);
  color: var(--color-text-inverse);
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.02em;
}

.homeCategoryList {
  list-style: none;
  margin: 0;
  padding: var(--space-3) 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.homeCategoryCard {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--blue-700);
  border-radius: var(--radius-xl);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-sm);
  padding: 10px;
}

.homeCategoryIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
}

.homeCategoryLabel {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-inverse);
}

/* ─── Tab bar (Inicio / Perfil) ─── */

.homeTabBar {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: var(--space-1);
  padding: 7px 10px 9px;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border-subtle);
}

.homeTab {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 5px 11px;
  border-radius: var(--radius-full);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 8px;
  color: var(--color-text-subtle);
}

.homeTab svg {
  width: 14px;
  height: 14px;
}

.homeTabActive {
  background: var(--blue-50);
  color: var(--blue-500);
  font-weight: 700;
}

.homeTabLabel {
  letter-spacing: -0.005em;
}

/* ════════════════════════════════════════════════════════════════════════════
   Mockup: Filtros — bottom sheet de obras sociales
   ════════════════════════════════════════════════════════════════════════════ */

.filtersSurface {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--blue-700);
  overflow: hidden;
}

.filtersTopBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 28px 12px 12px;
  color: var(--color-text-inverse);
}

.filtersBack {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 13px;
  color: var(--color-text-inverse);
}

.filtersBackTitle {
  letter-spacing: -0.01em;
}

.filtersTrigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.92);
}

.filtersSheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 50px;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.filtersSheetHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 8px;
  border-bottom: 1px solid var(--color-border-subtle);
}

.filtersSheetTitle {
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 14px;
  letter-spacing: -0.01em;
  color: var(--color-text);
}

.filtersClose {
  display: inline-flex;
  color: var(--color-text);
}

.filtersScroll {
  flex: 1;
  overflow: hidden;
  padding: 10px 12px 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filtersSectionLabel {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 8px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-text-subtle);
}

.filtersSectionHelp {
  margin: 0 0 4px;
  font-family: var(--font-body);
  font-size: 8px;
  line-height: 1.4;
  color: var(--color-text-muted);
}

.filtersGroup {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 6px;
}

.filtersGroupLabel {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 8px;
  color: var(--color-text-muted);
}

.filtersChipRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.filtersChip {
  font-family: var(--font-body);
  font-size: 8px;
  font-weight: 500;
  color: var(--color-text);
  background: transparent;
  border: 1px solid var(--color-border);
  padding: 4px 8px;
  border-radius: var(--radius-full);
  white-space: nowrap;
}

.filtersChipActive {
  background: var(--blue-50);
  border-color: var(--blue-500);
  color: var(--blue-600);
  font-weight: 600;
}

.filtersFooter {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 12px;
  border-top: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
}

.filtersBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 10px;
  padding: 8px 10px;
}

.filtersBtnGhost {
  flex: 0 0 auto;
  padding-inline: 16px;
  border: 1px solid var(--color-border);
  color: var(--color-text);
  background: var(--color-surface);
}

.filtersBtnPrimary {
  flex: 1;
  background: var(--blue-500);
  color: var(--color-text-inverse);
}
